import type { ReactNode, ScriptHTMLAttributes, SyntheticEvent } from "react";
import { useEffect } from "react";

type NextScriptProps = ScriptHTMLAttributes<HTMLScriptElement> & {
  readonly id?: string;
  readonly strategy?: "afterInteractive" | "lazyOnload" | "beforeInteractive";
  readonly onReady?: () => void;
  readonly children?: ReactNode;
};

const Script = ({
  id,
  src,
  onLoad,
  onError,
  onReady,
  children,
  strategy: _strategy,
  ...rest
}: NextScriptProps) => {
  useEffect(() => {
    const script = document.createElement("script");

    if (id) {
      const existing = document.getElementById(id);
      if (existing) {
        onReady?.();
        return undefined;
      }

      script.id = id;
    }

    Object.entries(rest).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      script.setAttribute(key, String(value));
    });

    if (src) {
      script.src = src;
      script.async = true;
    }

    if (typeof children === "string") {
      script.text = children;
    }

    const handleLoad = (event: Event) => {
      onLoad?.(event as unknown as SyntheticEvent<HTMLScriptElement>);
      onReady?.();
    };

    const handleError = (event: Event) => {
      onError?.(event as unknown as SyntheticEvent<HTMLScriptElement>);
    };

    script.addEventListener("load", handleLoad);
    script.addEventListener("error", handleError);

    document.body.appendChild(script);

    return () => {
      script.removeEventListener("load", handleLoad);
      script.removeEventListener("error", handleError);
      script.remove();
    };
  }, [id, src, onLoad, onError, onReady, children]);

  return null;
};

export default Script;
